import { useRef, useState, type FormEvent } from "react";

/**
 * PasteBox — the hero of the home screen.
 *
 * Takes free text (URL or text containing a URL), hands it to a parent
 * callback which owns the parseUrl → fetchMetadata → buildItem chain.
 * This component's only job is the UI primitive: an inviting textarea
 * and a submit affordance.
 *
 * Constitutional (Principle II): this is THE visual hero. Not a sidebar
 * input, not a modal.
 */
export interface PasteBoxProps {
  onSubmit: (text: string) => void;
  disabled?: boolean;
}

export function PasteBox({ onSubmit, disabled }: PasteBoxProps) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  function handle(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = value.trim();
    if (!text) return;
    onSubmit(text);
    setValue("");
    ref.current?.focus();
  }

  return (
    <form className="paste-box" onSubmit={handle}>
      <label htmlFor="night-light-paste" className="paste-box-hint">
        Paste an archive.org video URL
      </label>
      <textarea
        id="night-light-paste"
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="https://archive.org/details/…"
        autoFocus
        spellCheck={false}
        disabled={disabled}
        rows={2}
      />
      <div className="paste-box-row">
        <span className="paste-box-hint">
          or paste a block of text containing a link — we&apos;ll find it.
        </span>
        <button type="submit" disabled={disabled || !value.trim()}>
          {disabled ? "Loading…" : "Add to queue"}
        </button>
      </div>
    </form>
  );
}
